// components/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';

// Components (same as ProductView)
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";      // For cart modal in Nav
import { CreditCard, Smartphone, Lock, Check, MapPin, User, Mail, Phone, ShoppingBag, ArrowLeft,  Clock, Send, MessageSquare, HelpCircle, CheckCircle, FileText, Calendar, ChevronRight, ChevronDown, AlertCircle, Shield, Scale, Eye } from 'lucide-react';

const CACHE_KEY = 'checkout_page_cache';
const CACHE_TTL = 24*60*60 * 1000; // 1 minute

const CheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Layout configs – start null, filled from cache instantly
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [checkoutConfig, setCheckoutConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [cartItems, setCartItems] = useState([]); 

  const defaultConfig = {
    activeDesign: 'side-by-side', // 'side-by-side', 'wizard', 'compact'
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = { ...defaultConfig, ...checkoutConfig };
  const {
    activeDesign,
    title,
    shippingTitle,
    paymentTitle,
    reviewTitle,
    emptyCartMessage,
    continueShoppingText,
    checkoutText,
    customStyles,
  } = finalConfig;

  // ────────────────────────────────────────────────
  // Cache Helpers
  // ────────────────────────────────────────────────
  const getCached = () => {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) return null;
    try {
      const { data, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp < CACHE_TTL) return data;
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCached = (data) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  };

  // ────────────────────────────────────────────────
  // Load Layout → Cache FIRST, then background API refresh
  // ────────────────────────────────────────────────


const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email Us',
      value: 'support@yourstore.com',
      description: 'Send us an email anytime',
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: '+254 712 345 678',
      description: 'Mon-Fri from 8am to 6pm',
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: '123 Main Street, Nairobi',
      description: 'Kenya, East Africa',
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: 'Mon - Fri: 8:00 AM - 6:00 PM',
      description: 'Sat: 9:00 AM - 4:00 PM',
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    }
  ];

  const inquiryTypes = [
    {
      value: 'general',
      icon: MessageSquare,
      label: 'General Inquiry',
      description: 'Questions about our services'
    },
    {
      value: 'support',
      icon: HelpCircle,
      label: 'Customer Support',
      description: 'Need help with an order'
    },
    {
      value: 'sales',
      icon: ShoppingBag,
      label: 'Sales',
      description: 'Interested in bulk orders'
    }
  ];

  const faqs = [
    {
      question: 'What are your shipping options?',
      answer: 'We offer standard (3-5 days) and express (1-2 days) shipping across Kenya.'
    },
    {
      question: 'How can I track my order?',
      answer: 'You will receive a tracking number via email once your order ships.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We offer 30-day hassle-free returns on all products.'
    }
  ];


  

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // Safe config handling
  const actualCheckoutConfig = 
    checkoutConfig?.checkoutSettings && typeof checkoutConfig.checkoutSettings === 'object' && !Array.isArray(checkoutConfig.checkoutSettings)
      ? checkoutConfig.checkoutSettings
      : (typeof checkoutConfig === 'object' && !Array.isArray(checkoutConfig) ? checkoutConfig : {});

  const checkoutDefault = {
    activeDesign: 'side-by-side',
    devMode: false,
    customStyles: {},
  };

  const finalCheckoutConfig = { ...checkoutDefault, ...actualCheckoutConfig };
  const { activeDesign: checkoutDesign, devMode: checkoutDevMode, customStyles: checkoutStyles } = finalCheckoutConfig;

  const containerStyles = {
    background: checkoutDevMode && checkoutStyles.background ? checkoutStyles.background : undefined,
    color: checkoutDevMode && checkoutStyles.textColor ? checkoutStyles.textColor : undefined,
  };

 

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  const shipping = subtotal > 100 ? 0 : 10;
  const tax = subtotal * 0.16;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

 const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: Check,
      content: `By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.

These Terms and Conditions govern your use of our website and services. By creating an account, making a purchase, or using any part of our platform, you acknowledge that you have read, understood, and agree to be legally bound by these terms.

We reserve the right to modify these terms at any time. Your continued use of the website after changes are posted constitutes acceptance of the modified terms.`
    },
    {
      id: 'account',
      title: 'User Accounts',
      icon: Eye,
      content: `When you create an account with us, you must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account.

You are responsible for safeguarding the password you use to access the service and for any activities or actions under your password. You agree not to disclose your password to any third party and must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.

You may not use as a username the name of another person or entity that is not lawfully available for use, or a name or trademark that is subject to any rights of another person or entity without appropriate authorization.`
    },
    {
      id: 'purchases',
      title: 'Purchases and Payments',
      icon: Scale,
      content: `All purchases through our website are subject to product availability. We reserve the right to limit the quantity of items purchased per person, household, or order. We may also refuse or cancel any order at our discretion.

Prices for our products are subject to change without notice. We reserve the right to modify or discontinue any product without notice. We shall not be liable to you or any third party for any modification, price change, suspension, or discontinuance of any product.

Payment must be received by us before your order is dispatched. We accept major credit cards, debit cards, and M-Pesa payments. All payment information is encrypted and processed securely through our payment processors.

By submitting your payment information, you represent and warrant that you are authorized to use the designated payment method and authorize us to charge the total amount of your order to that payment method.`
    },
    {
      id: 'shipping',
      title: 'Shipping and Delivery',
      icon: FileText,
      content: `We offer various shipping options at checkout, including standard and express delivery. Delivery times are estimates and are not guaranteed. We are not responsible for delays caused by customs, weather conditions, or carrier issues.

Risk of loss and title for items purchased pass to you upon delivery to the carrier. We are not responsible for lost or stolen packages confirmed delivered to the address entered for an order.

International shipments may be subject to import taxes, customs duties, and fees levied by the destination country. These charges are the recipient's responsibility and are not included in our pricing.

You must provide a complete and accurate delivery address. We cannot be held responsible for orders delivered to incorrect addresses provided by the customer.`
    },
    {
      id: 'returns',
      title: 'Returns and Refunds',
      icon: AlertCircle,
      content: `We want you to be completely satisfied with your purchase. If you are not satisfied, you may return most items within 30 days of delivery for a full refund or exchange.

Items must be returned in their original condition, unworn, unwashed, and with all original tags attached. Items that show signs of wear, damage, or alteration cannot be accepted for return.

To initiate a return, log into your account and follow the return instructions. You will receive a return shipping label via email. Original shipping charges are non-refundable unless the return is due to our error.

Refunds will be processed to the original payment method within 5-7 business days after we receive and inspect the returned item. Sale items and gift cards are final sale and cannot be returned or exchanged.

Custom or personalized items cannot be returned unless they are defective or we made an error in fulfilling your order.`
    },
    {
      id: 'intellectual',
      title: 'Intellectual Property',
      icon: Shield,
      content: `The service and its original content, features, and functionality are and will remain the exclusive property of our company and its licensors. The service is protected by copyright, trademark, and other laws.

Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent. You may not copy, reproduce, distribute, or create derivative works from any content on our website without explicit permission.

User-generated content, such as reviews and comments, remains your property. However, by posting content to our site, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, adapt, publish, and distribute such content.`
    },
    {
      id: 'prohibited',
      title: 'Prohibited Uses',
      icon: AlertCircle,
      content: `You may not use our website for any illegal purpose or to violate any laws in your jurisdiction. You agree not to:

• Use the service in any way that could damage, disable, overburden, or impair the service
• Attempt to gain unauthorized access to any portion of the service
• Use any automated system to access the service
• Transmit any viruses, worms, or malicious code
• Impersonate another person or entity
• Harass, abuse, or harm another person
• Collect or store personal data about other users without permission
• Use the service for any commercial purpose without our consent

We reserve the right to terminate your account and refuse service if you violate these prohibited uses.`
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      icon: Scale,
      content: `To the maximum extent permitted by applicable law, our company shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.

Our total liability to you for all claims arising from or relating to the service shall not exceed the amount you paid us in the twelve (12) months prior to the action giving rise to liability.

We do not warrant that the service will be uninterrupted, timely, secure, or error-free. We make no warranty regarding the quality, accuracy, or reliability of any products, services, or information obtained through the service.

Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for consequential damages. In such jurisdictions, our liability will be limited to the maximum extent permitted by law.`
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      icon: Shield,
      content: `Your privacy is important to us. Our Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service. Please review our Privacy Policy carefully.

By using the service, you consent to the data practices described in our Privacy Policy. We collect information you provide directly, such as when you create an account, make a purchase, or contact customer service.

We also automatically collect certain information about your device when you use our service, including IP address, browser type, and browsing behavior. This information helps us improve our service and provide a better user experience.

We use industry-standard security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.`
    },
    {
      id: 'disputes',
      title: 'Dispute Resolution',
      icon: Scale,
      content: `Any disputes arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiations between the parties.

If negotiations fail, disputes shall be resolved through binding arbitration in accordance with the rules of the arbitration association in our jurisdiction. Each party shall bear their own costs of arbitration.

You agree to waive your right to participate in a class action lawsuit or class-wide arbitration. All claims must be brought in the parties' individual capacity and not as a plaintiff or class member in any purported class action.

These Terms and any dispute arising from them shall be governed by and construed in accordance with the laws of Kenya, without regard to its conflict of law provisions.`
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: AlertCircle,
      content: `We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including but not limited to breach of these Terms.

Upon termination, your right to use the service will immediately cease. If you wish to terminate your account, you may do so by contacting customer service or through your account settings.

All provisions of the Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.`
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      icon: Calendar,
      content: `We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.

What constitutes a material change will be determined at our sole discretion. By continuing to access or use our service after revisions become effective, you agree to be bound by the revised terms.

If you do not agree to the new terms, you must stop using the service and may close your account. Your continued use of the service following the posting of revised Terms means that you accept and agree to the changes.`
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: FileText,
      content: `If you have any questions about these Terms and Conditions, please contact us:

Email: legal@yourstore.com
Phone: +254 712 345 678
Address: 123 Main Street, Nairobi, Kenya

For general customer service inquiries, please visit our Help Center or contact support@yourstore.com.

Our customer service team is available Monday through Friday, 8:00 AM to 6:00 PM EAT.`
    }
  ];

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };


  // Design 1: Side-by-Side Layout
  const Design1 = () => (
       <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Terms & Conditions
              </h1>
              <p className="text-gray-600 flex items-center gap-2 flex-wrap">
                <Calendar className="w-4 h-4" />
                Last updated: January 15, 2024
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
            <p className="text-sm text-blue-900">
              <strong>Important:</strong> Please read these terms and conditions carefully before using our services. 
              By accessing or using our website, you agree to be bound by these terms.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Jump to:</span>
            <div className="flex gap-2">
              {sections.slice(0, 5).map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full whitespace-nowrap transition-colors"
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Introduction */}
        <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm mb-6">
          <p className="text-gray-700 leading-relaxed">
            Welcome to our e-commerce platform. These Terms and Conditions outline the rules and regulations 
            for the use of our website and services. By accessing this website, we assume you accept these 
            terms and conditions. Do not continue to use our website if you do not agree to all of the terms 
            and conditions stated on this page.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;

            return (
              <div
                key={section.id}
                id={section.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden scroll-mt-32"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-sm font-semibold text-gray-500">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-6 pb-6 md:px-16 md:pb-8">
                    <div className="prose prose-sm max-w-none">
                      {section.content.split('\n\n').map((paragraph, pIdx) => {
                        // Check if paragraph is a bullet list
                        if (paragraph.includes('•')) {
                          const items = paragraph.split('\n').filter(line => line.trim());
                          return (
                            <ul key={pIdx} className="space-y-2 my-4">
                              {items.map((item, iIdx) => (
                                <li key={iIdx} className="flex items-start gap-2 text-gray-700">
                                  <span className="text-gray-900 font-bold mt-1">•</span>
                                  <span>{item.replace('• ', '')}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        
                        return (
                          <p key={pIdx} className="text-gray-700 leading-relaxed mb-4">
                            {paragraph}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-12 bg-gray-900 text-white rounded-xl p-6 md:p-8">
          <h3 className="text-xl font-bold mb-3">Have Questions?</h3>
          <p className="text-gray-300 mb-6">
            If you have any questions about these Terms and Conditions, please don't hesitate to contact us. 
            Our legal team is available to help clarify any concerns you may have.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Contact Legal Team
            </button>
            <button className="border-2 border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
              View Privacy Policy
            </button>
          </div>
        </div>

        {/* Back to Top */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 mx-auto"
          >
            ↑ Back to Top
          </button>
        </div>
      </div>
    </div>
  );

  // Design 2: Multi-Step Wizard (Guided Flow)
  const Design2 = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex justify-between mb-12 max-w-md mx-auto">
          {['Shipping', 'Payment', 'Review'].map((label, index) => (
            <div key={label} className="flex-1 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto font-bold text-lg transition-all ${
                  step > index + 1
                    ? 'bg-green-600 text-white'
                    : step === index + 1
                    ? 'bg-gray-900 text-white ring-4 ring-gray-200'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step > index + 1 ? <Check size={24} /> : index + 1}
              </div>
              <p className={`mt-3 font-medium ${step === index + 1 ? 'text-gray-900' : 'text-gray-500'}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{shippingTitle}</h2>
              {/* Shipping form */}
              <button
                onClick={() => setStep(2)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Continue to Payment
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{paymentTitle}</h2>
              {/* Payment selection */}
              <button
                onClick={() => setStep(3)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Review & Confirm
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{reviewTitle}</h2>
              {/* Summary */}
              <button
                onClick={handleSubmit}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                {checkoutText} - ${total.toFixed(2)}
              </button>
            </>
          )}
        </div>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="mt-6 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}
      </div>
    </div>
  );

  // Design 3: Compact Mobile-First
  const Design3 = () => (
    <div className={`min-h-screen ${customStyles.background} py-8 px-4`}>
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">{title}</h2>

        {/* Compact shipping + payment + summary */}
        <div className="space-y-8">
          {/* Shipping */}
          <div>
            <h3 className="text-xl font-bold mb-4">{shippingTitle}</h3>
            {/* Compact form */}
          </div>

          {/* Payment */}
          <div>
            <h3 className="text-xl font-bold mb-4">{paymentTitle}</h3>
            {/* Compact payment */}
          </div>

          {/* Summary */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-bold mb-4">Summary</h3>
            <div className="flex justify-between font-bold text-xl">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
          >
            {checkoutText}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCheckoutDesign = () => {
    switch (checkoutDesign) {
      case 'side-by-side': return <Design1 />;
      case 'wizard': return <Design2 />;
      case 'compact': return <Design3 />;
      default: return <Design1 />;
    }
  };

 

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Flash Bar */}
    <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
        font-family: ${config.FONT_FAMILY}, sans-serif !important;
     }
      `}
      </style>
      <br/><br/><br/><br/><br/>
      {flashConfig && <FlashBar config={flashConfig} />}

      {/* Navigation with Cart integration */}
      {navConfig && (
        <NavigationBar 
          config={navConfig} 
          cartConfig={cartConfig} 
        />
      )}

      {/* Floating Cart */}
      {cartConfig && <CartSection config={cartConfig} isOpen={false} onClose={() => {}} />}

   
      

     
      <main className="flex-grow" style={containerStyles}>
        {renderCheckoutDesign()}
      </main>
       

      {/* Footer */}
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default CheckoutPage;